import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail, API_URL } from "@/lib/api";
import { STATUS_LABELS } from "@/lib/constants";

const inputCls =
  "w-full bg-[#0A1628] border border-[#2C4066] rounded px-4 py-3 text-sm text-white focus:outline-none focus:border-[#51ADDA] placeholder:text-slate-600";
const labelCls = "block font-mono text-xs uppercase tracking-wider text-slate-300 mb-2";

const STATUS_COLORS = {
  recue: "bg-amber-400/10 text-amber-400 border-amber-400/30",
  devis_envoye: "bg-[#51ADDA]/10 text-[#51ADDA] border-[#51ADDA]/30",
  payee: "bg-emerald-400/10 text-emerald-400 border-emerald-400/30",
  terminee: "bg-slate-400/10 text-slate-300 border-slate-500/30",
  annulee: "bg-red-400/10 text-red-400 border-red-400/30",
};

const DevisCard = ({ d, edit, onEdit, onSave, saving }) => (
  <div data-testid={`admin-devis-card-${d.reference}`} className="bg-[#0F1D36] border border-[#1C2C4E] rounded-xl p-6 space-y-4">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <span className="font-display text-2xl text-white tracking-wider">{d.reference}</span>
        <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded border ${STATUS_COLORS[d.status]}`}>
          {STATUS_LABELS[d.status]}
        </span>
      </div>
      <span className="font-mono text-xs text-slate-500">{new Date(d.created_at).toLocaleString("fr-FR")}</span>
    </div>

    <div className="grid md:grid-cols-3 gap-x-6 gap-y-2 text-sm">
      {[
        ["Calculateur", d.ecu_reference],
        ["Véhicule", `${d.vehicle} (${d.year})`],
        ["Motorisation", `${d.motorisation} — ${d.energie}${d.puissance ? ` — ${d.puissance}` : ""}`],
        ["Prestation", d.prestation],
        ["Email", d.email],
        ["Téléphone", d.telephone || "—"],
      ].map(([k, v]) => (
        <div key={k} className="flex justify-between gap-3 border-b border-[#1C2C4E] pb-1">
          <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{k}</span>
          <span className="text-slate-200 text-right break-all">{v}</span>
        </div>
      ))}
    </div>

    {d.price != null && d.status !== "recue" && d.status !== "annulee" && (
      <div className="flex flex-wrap items-center gap-3 p-3 rounded bg-[#0A1628] border border-[#1C2C4E]">
        <span className="font-mono text-[10px] uppercase tracking-widest text-[#51ADDA]">Lien client (suivi & paiement)</span>
        <code className="text-xs text-slate-300 break-all flex-1">
          {`${window.location.origin}/suivi?reference=${d.reference}&email=${encodeURIComponent(d.email)}`}
        </code>
        <button
          type="button"
          data-testid={`admin-copy-link-${d.reference}`}
          onClick={() => {
            navigator.clipboard.writeText(`${window.location.origin}/suivi?reference=${d.reference}&email=${encodeURIComponent(d.email)}`);
            toast.success("Lien copié — envoyez-le au client (SMS, WhatsApp...)");
          }}
          className="text-xs font-mono uppercase tracking-wider border border-[#2C4066] rounded px-3 py-2 text-slate-300 hover:border-[#51ADDA] hover:text-white transition-colors"
        >
          Copier
        </button>
      </div>
    )}

    {d.photo_path && (
      <div className="flex items-center gap-4">
        <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Photo boîtier</span>
        <a href={`${API_URL}/devis/photo/${d.photo_path}`} target="_blank" rel="noreferrer" data-testid={`admin-photo-link-${d.reference}`}>
          <img src={`${API_URL}/devis/photo/${d.photo_path}`} alt="Photo du boîtier client" className="h-16 w-16 object-cover rounded border border-[#2C4066] hover:border-[#51ADDA] transition-colors" />
        </a>
      </div>
    )}

    <div className="grid md:grid-cols-4 gap-4 items-end pt-2">
      <div>
        <label className={labelCls}>Prix du devis (€)</label>
        <input
          type="number"
          min="0"
          step="0.01"
          data-testid={`admin-price-input-${d.reference}`}
          value={edit.price}
          onChange={(e) => onEdit(d, { price: e.target.value })}
          placeholder="149.00"
          className={inputCls}
        />
      </div>
      <div>
        <label className={labelCls}>Statut</label>
        <select
          data-testid={`admin-status-select-${d.reference}`}
          value={edit.status}
          onChange={(e) => onEdit(d, { status: e.target.value })}
          className={inputCls}
        >
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>
      <div>
        <label className={labelCls}>Note client (optionnel)</label>
        <input
          type="text"
          data-testid={`admin-note-input-${d.reference}`}
          value={edit.admin_note}
          onChange={(e) => onEdit(d, { admin_note: e.target.value })}
          placeholder="Message visible par le client"
          className={inputCls}
        />
      </div>
      <button
        type="button"
        data-testid={`admin-save-button-${d.reference}`}
        disabled={saving}
        onClick={() => onSave(d)}
        className="bg-[#51ADDA] text-[#0A1628] font-display uppercase tracking-wider py-3 px-6 rounded hover:bg-white transition-colors font-bold disabled:opacity-50"
      >
        {saving ? "..." : "Enregistrer"}
      </button>
    </div>
  </div>
);

export default function Admin() {
  const [auth, setAuth] = useState(null); // null = vérification, false = non connecté
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [devisList, setDevisList] = useState([]);
  const [edits, setEdits] = useState({});
  const [savingId, setSavingId] = useState(null);

  const loadDevis = useCallback(async () => {
    try {
      const r = await api.get("/admin/devis");
      setDevisList(r.data);
    } catch {
      setAuth(false);
    }
  }, []);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setAuth(r.data))
      .catch(() => setAuth(false));
  }, []);

  useEffect(() => {
    if (auth) loadDevis();
  }, [auth, loadDevis]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const r = await api.post("/auth/login", { email, password });
      setAuth(r.data);
      toast.success("Connecté");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setAuth(false);
    setDevisList([]);
  };

  const getEdit = (d) =>
    edits[d.id] || { price: d.price ?? "", status: d.status, admin_note: d.admin_note || "" };

  const onEdit = (d, patch) =>
    setEdits((prev) => ({ ...prev, [d.id]: { ...getEdit(d), ...prev[d.id], ...patch } }));

  const save = async (d) => {
    const e = getEdit(d);
    setSavingId(d.id);
    try {
      const price = e.price === "" ? null : parseFloat(e.price);
      let status = e.status;
      if (price != null && !Number.isNaN(price) && status === "recue") status = "devis_envoye";
      await api.patch(`/admin/devis/${d.id}`, { price, status, admin_note: e.admin_note });
      toast.success(`Devis ${d.reference} mis à jour`);
      setEdits((prev) => {
        const next = { ...prev };
        delete next[d.id];
        return next;
      });
      loadDevis();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingId(null);
    }
  };

  if (auth === null) {
    return (
      <div className="bg-[#0A1628] min-h-screen flex items-center justify-center text-slate-500 font-mono text-xs uppercase tracking-widest">
        Chargement...
      </div>
    );
  }

  if (auth === false) {
    return (
      <div className="bg-[#0A1628] text-[#e2e8f0] min-h-screen font-body flex items-center justify-center px-6">
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-md bg-[#0F1D36] border border-[#1C2C4E] rounded-xl p-8 md:p-10 space-y-6">
          <div className="text-center">
            <span className="font-mono text-xs uppercase tracking-[0.14em] text-[#51ADDA] block mb-2">Espace Pro</span>
            <h1 className="font-display text-3xl uppercase tracking-tight text-white">Connexion Atelier</h1>
          </div>
          <div>
            <label htmlFor="admin-email" className={labelCls}>Email</label>
            <input id="admin-email" data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="admin@atelier.fr" />
          </div>
          <div>
            <label htmlFor="admin-password" className={labelCls}>Mot de passe</label>
            <input id="admin-password" data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="••••••••" />
          </div>
          {error && (
            <div data-testid="admin-error-alert" className="p-4 rounded bg-red-400/10 border border-red-400/30 text-red-400 text-sm">{error}</div>
          )}
          <button type="submit" data-testid="admin-login-button" className="w-full bg-[#51ADDA] text-[#0A1628] font-display uppercase tracking-wider text-lg py-3 rounded hover:bg-white transition-colors font-bold">
            Se connecter
          </button>
          <Link to="/" data-testid="admin-back-link" className="block text-center text-xs font-mono uppercase tracking-widest text-slate-500 hover:text-white transition-colors">
            ← Retour à la boutique
          </Link>
        </form>
      </div>
    );
  }

  const pending = devisList.filter((d) => d.status === "recue").length;
  const toPay = devisList.filter((d) => d.status === "devis_envoye").length;
  const paid = devisList.filter((d) => d.status === "payee" || d.status === "terminee").length;

  return (
    <div className="bg-[#0A1628] text-[#e2e8f0] min-h-screen font-body antialiased">
      <nav className="border-b border-[#1C2C4E] px-6 py-4 sticky top-0 bg-[#0A1628]/90 backdrop-blur z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <span className="font-display tracking-wider text-base sm:text-xl uppercase text-white whitespace-nowrap">NEOELEC AUTO — Atelier</span>
          <div className="flex items-center gap-6">
            <span className="hidden sm:inline font-mono text-xs text-slate-500">{auth.email}</span>
            <Link to="/" data-testid="admin-shop-link" className="text-xs uppercase tracking-[0.14em] text-slate-400 hover:text-white transition-colors">
              Boutique
            </Link>
            <button
              type="button"
              data-testid="admin-logout-button"
              onClick={logout}
              className="text-xs uppercase tracking-[0.14em] border border-[#2C4066] rounded px-4 py-2 text-slate-300 hover:border-[#51ADDA] hover:text-white transition-colors"
            >
              Déconnexion
            </button>
          </div>
        </div>
      </nav>

      <main data-testid="admin-dashboard" className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex flex-wrap gap-4 mb-10">
          {[
            ["Demandes à traiter", pending, "text-amber-400"],
            ["Devis en attente de règlement", toPay, "text-[#51ADDA]"],
            ["Payées / terminées", paid, "text-emerald-400"],
          ].map(([label, value, color]) => (
            <div key={label} className="px-5 py-4 rounded-lg bg-[#0F1D36] border border-[#1C2C4E]">
              <span className={`font-display text-3xl ${color}`}>{value}</span>
              <span className="block font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-1">{label}</span>
            </div>
          ))}
        </div>

        <h1 className="font-display text-3xl uppercase tracking-tight text-white mb-6">Demandes de devis</h1>

        {devisList.length === 0 ? (
          <div data-testid="admin-empty-state" className="p-8 rounded-lg border border-dashed border-[#2C4066] text-center text-slate-500 text-sm">
            Aucune demande pour le moment. Les demandes du formulaire apparaîtront ici.
          </div>
        ) : (
          <div data-testid="admin-devis-list" className="space-y-6">
            {devisList.map((d) => (
              <DevisCard key={d.id} d={d} edit={getEdit(d)} onEdit={onEdit} onSave={save} saving={savingId === d.id} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
